import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FileText, Plus, Edit, Trash2, Eye, Calendar } from 'lucide-react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Badge } from '../../components/ui/badge';
import api from '../../utils/api';
import { getUser } from '../../utils/auth';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ContentManagement = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const user = getUser();
  const [loading, setLoading] = useState(true);
  const [blogPosts, setBlogPosts] = useState([]);
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    if (user?.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    fetchContent();
  }, []);

  const fetchContent = async () => {
    try {
      const [blogRes, deptRes] = await Promise.all([
        api.get('/blog'),
        api.get('/departments')
      ]);
      setBlogPosts(blogRes.data);
      setDepartments(deptRes.data);
    } catch (error) {
      toast.error(i18n.language === 'ar' ? 'فشل تحميل المحتوى' : 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="spinner" /></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <Navbar />
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-4xl font-bold mb-2">{i18n.language === 'ar' ? 'إدارة المحتوى' : 'Content Management'}</h1>
              <p className="text-text-secondary text-lg">{i18n.language === 'ar' ? 'المدونة والأخبار والخدمات' : 'Blog, news and services'}</p>
            </div>
            <Button className="btn-primary gap-2">
              <Plus className="w-5 h-5" />
              {i18n.language === 'ar' ? 'محتوى جديد' : 'New Content'}
            </Button>
          </div>

          <Tabs defaultValue="blog">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="blog">{i18n.language === 'ar' ? 'المدونة' : 'Blog'}</TabsTrigger>
              <TabsTrigger value="departments">{i18n.language === 'ar' ? 'الأقسام' : 'Departments'}</TabsTrigger>
              <TabsTrigger value="services">{i18n.language === 'ar' ? 'الخدمات' : 'Services'}</TabsTrigger>
            </TabsList>

            <TabsContent value="blog" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'المقالات' : 'Articles'}</CardTitle>
                </CardHeader>
                <CardContent>
                  {blogPosts.length === 0 ? (
                    <div className="text-center py-20">
                      <FileText className="w-16 h-16 text-text-secondary mx-auto mb-4" />
                      <p className="text-text-secondary">{i18n.language === 'ar' ? 'لا توجد مقالات' : 'No articles yet'}</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {blogPosts.map((post) => (
                        <div key={post.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-lg">{i18n.language === 'ar' ? post.title_ar : post.title_en}</h3>
                              <Badge variant={post.is_published ? 'default' : 'secondary'}>
                                {post.is_published ? (i18n.language === 'ar' ? 'منشور' : 'Published') : (i18n.language === 'ar' ? 'مسودة' : 'Draft')}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-text-secondary">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {format(new Date(post.published_at), 'dd/MM/yyyy')}
                              </span>
                              <span>{i18n.language === 'ar' ? 'بواسطة:' : 'By:'} {post.author}</span>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline"><Eye className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline"><Edit className="w-4 h-4" /></Button>
                            <Button size="sm" variant="outline" className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="departments" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'الأقسام القانونية' : 'Legal Departments'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {departments.map((dept) => (
                      <div key={dept.id} className="p-4 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-lg">{i18n.language === 'ar' ? dept.name_ar : dept.name_en}</h3>
                          <Badge>{dept.is_active ? (i18n.language === 'ar' ? 'نشط' : 'Active') : (i18n.language === 'ar' ? 'غير نشط' : 'Inactive')}</Badge>
                        </div>
                        <p className="text-sm text-text-secondary line-clamp-2">{i18n.language === 'ar' ? dept.description_ar : dept.description_en}</p>
                        <div className="flex gap-2 mt-4">
                          <Button size="sm" variant="outline" className="flex-1"><Edit className="w-4 h-4" /></Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="services" className="mt-6">
              <Card>
                <CardHeader>
                  <CardTitle>{i18n.language === 'ar' ? 'الخدمات' : 'Services'}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-20">
                    <FileText className="w-16 h-16 text-text-secondary mx-auto mb-4" />
                    <p className="text-text-secondary">{i18n.language === 'ar' ? 'قيد التطوير' : 'Coming soon'}</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default ContentManagement;